/**
 * VueLayers
 * Web map Vue components with the power of OpenLayers
 *
 * @package vuelayers
 * @author Vladimir Vershinin <ghettovoice@gmail.com>
 * @version 0.11.37
 * @license MIT
 * @copyright (c) 2017-2021, Vladimir Vershinin <ghettovoice@gmail.com>
 */
import _mapInstanceProperty from '@babel/runtime-corejs3/core-js-stable/instance/map';
import _sliceInstanceProperty from '@babel/runtime-corejs3/core-js-stable/instance/slice';
import _findInstanceProperty from '@babel/runtime-corejs3/core-js-stable/instance/find';
import _sortInstanceProperty from '@babel/runtime-corejs3/core-js-stable/instance/sort';
import Collection from 'ol/Collection';
import Interaction from 'ol/interaction/Interaction';
import Vue from 'vue';
import { merge } from 'rxjs/_esm5/internal/observable/merge';
import { getInteractionId, getInteractionPriority, initializeInteraction } from '../ol-ext';
import { instanceOf } from '../util/assert';
import rxSubs from './rx-subs';
import identMap from './ident-map';
import { observableFromOlEvent } from '../rx-ext';

var interactionsContainer = {
  mixins: [identMap, rxSubs],
  computed: {
    interactionIds: function interactionIds() {
      var _context;

      if (!this.rev) return [];
      return _mapInstanceProperty(_context = this.getInteractions()).call(_context, getInteractionId);
    },
    interactionsCollectionIdent: function interactionsCollectionIdent() {
      if (!this.olObjIdent) return;
      return this.makeIdent(this.olObjIdent, 'interactions_collection');
    }
  },
  methods: {
    /**
     * @param {Interaction|Vue} interaction
     * @return {void}
     */
    addInteraction: function addInteraction(interaction) {
      interaction = interaction instanceof Vue ? interaction.$interaction : interaction;
      instanceOf(interaction, Interaction);

      if (this.getInteractionById(getInteractionId(interaction)) == null) {
        initializeInteraction(interaction);
        this.$interactionsCollection.push(interaction);
        this.sortInteractions();
      }
    },

    /**
     * @param {Interaction|Vue} interaction
     * @return {void}
     */
    removeInteraction: function removeInteraction(interaction) {
      interaction = this.getInteractionById(getInteractionId(interaction));
      if (!interaction) return;
      this.$interactionsCollection.remove(interaction);
      this.sortInteractions();
    },

    /**
     * @return {Interaction[]}
     */
    getInteractions: function getInteractions() {
      var _context2;

      return _sliceInstanceProperty(_context2 = this.$interactionsCollection.getArray()).call(_context2);
    },

    /**
     * @return {Collection<Interaction>>}
     */
    getInteractionsCollection: function getInteractionsCollection() {
      return this._interactionsCollection;
    },

    /**
     * @param {string|number} interactionId
     * @return {Interaction|undefined}
     */
    getInteractionById: function getInteractionById(interactionId) {
      var _context3;

      return _findInstanceProperty(_context3 = this.$interactionsCollection.getArray()).call(_context3, function (interaction) {
        return getInteractionId(interaction) === interactionId;
      });
    },

    /**
     * @return {void}
     */
    sortInteractions: function sortInteractions(sorter) {
      var _context4;

      sorter || (sorter = this.getDefaultInteractionsSorter());

      _sortInstanceProperty(_context4 = this.$interactionsCollection.getArray()).call(_context4, sorter);
    },

    /**
     * @return {function}
     * @protected
     */
    getDefaultInteractionsSorter: function getDefaultInteractionsSorter() {
      // sort interactions by priority in asc order
      // the higher the priority, the earlier the interaction handles the event
      return function (a, b) {
        var ap = getInteractionPriority(a) || 0;
        var bp = getInteractionPriority(b) || 0;
        return ap === bp ? 0 : ap - bp;
      };
    },

    /**
     * @return {void}
     */
    clearInteractions: function clearInteractions() {
      this.$interactionsCollection.clear();
    },

    /**
     * @returns {Object}
     * @protected
     */
    getServices: function getServices() {
      var vm = this;
      return {
        get interactionsContainer() {
          return vm;
        }

      };
    }
  },
  created: function created() {
    this._interactionsCollection = this.instanceFactoryCall(this.interactionsCollectionIdent, function () {
      return new Collection();
    });
    defineServices.call(this);
    subscribeToCollectionEvents.call(this);
  }
};

function defineServices() {
  Object.defineProperties(this, {
    $interactionsCollection: {
      enumerable: true,
      get: this.getInteractionsCollection
    }
  });
}

function subscribeToCollectionEvents() {
  var _this = this;

  var adds = observableFromOlEvent(this.$interactionsCollection, 'add');
  var removes = observableFromOlEvent(this.$interactionsCollection, 'remove');
  this.subscribeTo(merge(adds, removes), function (_ref) {
    var type = _ref.type,
        element = _ref.element;
    ++_this.rev;

    _this.$nextTick(function () {
      _this.$emit(type + ':interaction', element);
    });
  });
}

export default interactionsContainer;
